import { Logger } from "../../logger/logger";
import { Converter } from "../converter";
import { Bit } from "../../helper/bit";

export class UrlHelper {
	private static observableLocationInternal: KnockoutObservable<string>;

	public static readonly getDataAttributeName = (dataAttribute: string) => {
		let result = dataAttribute;
		if (!dataAttribute.startsWith("data-")) {
			result = "data-" + result;
		}

		return result;
	}

	public static readonly getUrlDataAttributeName = (dataAttribute: string) => {
		let result = dataAttribute;
		if (!dataAttribute.endsWith("-url")) {
			result += "-url";
		}

		return UrlHelper.getDataAttributeName(result);
	}

	public static readonly getIconUrl = (icoId: number, size: string = "Exact24") => {
		if (icoId == null) {
			Logger.warn(`Invalid icoId=${icoId}`);
			// return an empty image
			return "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";
		}
		let iconUrl = `${UrlHelper.getDataUrl("icon")}?id=${icoId}&size=${size}`;
		return iconUrl;
	}

	public static readonly getData = (dataAttribute: string, selector: string = "body") => {
		let curDataAttribute = UrlHelper.getDataAttributeName(dataAttribute);
		let $selector = $(selector);
		if (!$selector || $selector.length <= 0 || !$selector.is(`[${curDataAttribute}]`)) {
			Logger.error(`Could not find selector '${selector}' or attribute '${curDataAttribute}'; document.readyState=${document.readyState}`);
		}

		return $selector.attr(curDataAttribute);
	}

	public static readonly getInt = (dataAttribute: string, selector?: string) => {
		let str = UrlHelper.getData(dataAttribute, selector);
		return str ? Converter.toInt(str) : null;
	}

	public static readonly getIntFromElem = (dataAttribute: string, elem: JQuery<HTMLElement>) => {
		let str = elem.data(dataAttribute);
		return str ? Converter.toInt(str) : null;
	}

	public static readonly getFloat = (dataAttribute: string, selector?: string) => {
		let str = UrlHelper.getData(dataAttribute, selector);
		return str ? Converter.toFloat(str) : null;
	}

	public static readonly getBool = (dataAttribute: string, selector?: string) => {
		let str = UrlHelper.getData(dataAttribute, selector);
		return str ? Converter.toBool(str) : null;
	}

	public static readonly getBoolOrDefault = (dataAttribute: string, defaultValue: boolean = false, selector?: string) => {
		let str = UrlHelper.getData(dataAttribute, selector);
		let result = str ? Converter.toBool(str) : null;

		return result ?? defaultValue;
	}

	public static readonly getDataUrl = (dataAttribute: string, selector: string = "body") => {
		let urlDataAttribute = UrlHelper.getUrlDataAttributeName(dataAttribute);
		let elements = $(selector);
		if (!elements || elements.length <= 0 || !elements.is(`[${urlDataAttribute}]`)) {
			Logger.error(`Could not find attribute '${urlDataAttribute}' or selector '${selector}'; document.readyState=${document.readyState}`);
		}
		let result = elements.attr(urlDataAttribute);
		if (!result || result.length <= 0) {
			Logger.warn(`Attribute '${urlDataAttribute}' on selector '${selector}' is empty. Maybe missed some value from server? document.readyState=${document.readyState}`);
		}

		// TODO: @net6 Remove this code and from @Url.RouteUrl(.., new { httpRoute = true }) after the upgrade
		let suffix = "httproute=True";
		if (result && result.toLocaleLowerCase().endsWith(suffix.toLocaleLowerCase())) {
			result = result.substr(0, result.length - suffix.length);
			if (result.endsWith("?")) {
				result = result.substr(0, result.length - 1);
			}
		}

		return result;
	}

	public static readonly getBaseUrl = () => {
		let baseUrl = UrlHelper.getDataUrl("base");
		if (!baseUrl || !(baseUrl.length > 0)) {
			baseUrl = "/";
		}
		return baseUrl;
	}

	public static readonly getCurrentUrl = () => {
		let currentUrl = window.location.href;
		return currentUrl;
	}

	public static readonly getOdataUrl = (selector: string = "#tabs") => {
		return UrlHelper.getDataUrl("odata", selector);
	}

	public static readonly getUploadUrl = () => {
		return UrlHelper.getDataUrl("upload");
	}

	public static readonly getUploadFileHandleUrl = () => {
		return UrlHelper.getDataUrl("upload-file-handled");
	}

	public static readonly getStateStoringUrl = () => {
		return UrlHelper.getDataUrl("statestoring");
	}

	public static readonly getUserSettings = () => {
		return UrlHelper.getDataUrl("get-user-settings");
	};

	public static readonly getUserAccountId = () => {
		return UrlHelper.getInt("userid");
	}

	public static readonly getUserMarketerId = () => {
		return UrlHelper.getInt("user-marketerid");
	}

	public static readonly getUserId = () => {
		return UrlHelper.getInt("user-id");
	}

	public static readonly getApplicationInstanceHash = () => {
		return UrlHelper.getData("application-instance-hash");
	}

	public static readonly getApplicationSettingUrl = () => {
		return UrlHelper.getDataUrl("application-setting");
	}

	public static readonly getQueue = () => {
		return UrlHelper.getDataUrl("queue-get");
	}

	public static readonly getQueueStatus = () => {
		return UrlHelper.getDataUrl("queue-status");
	}

	public static readonly getInstanceConfigurationUrl = () => {
		return UrlHelper.getDataUrl("instance-configuration");
	}

	public static readonly getInstanceLanguageIdUrl = () => {
		return UrlHelper.getDataUrl("instance-language-id");
	}

	public static readonly getInstanceCurrencyIdUrl = () => {
		return UrlHelper.getDataUrl("instance-currency-id");
	}

	public static readonly getAgencyReportingDownloadUrl = () => {
		return UrlHelper.getDataUrl("agency-reporting-download");
	}

	public static readonly getModuleId = () => {
		return UrlHelper.getInt("module-id", "#tabs");
	}

	public static readonly getCacheId = () => {
		return UrlHelper.getData("cache-id");
	}

	public static readonly getLocalizationUrl = () => {
		return UrlHelper.getDataUrl("localization");
	}

	public static readonly getCulture = () => {
		return UrlHelper.getData("culture");
	}

	public static readonly getTimeDiffInMs = () => {
		return UrlHelper.getInt("db-time-diff-in-ms");
	}

	public static readonly isDebuggingEnabled = () => {
		return UrlHelper.getBool("is-debugging-enabled");
	}

	public static readonly replaceUrl = (url: string) => {
		window.history.replaceState({}, "", url);
	}

	public static readonly isMultiMarketerEnabled = () => {
		return UrlHelper.getBool("is-multi-marketer-enabled");
	}

	public static readonly getAllowedMarketerIds = () => {
		return UrlHelper.getInt("user-allowed-markter-ids");
	}

	public static readonly hasMarketerPermission = (mktIdSet: number) => {
		return Bit.and(UrlHelper.getAllowedMarketerIds(), mktIdSet) !== 0;
	}

	public static get observableLocation() {
		if (!this.observableLocationInternal) {
			this.observableLocationInternal = ko.observable(window.location.href);

			let triggerEvent = () => {
				let newLocation = window.location.href;
				if (this.observableLocationInternal() !== newLocation) {
					this.observableLocationInternal(newLocation);
				}
			};

			$(window).on("hashchange", triggerEvent);

			let pushStateImpl = history.pushState;
			history.pushState = function (...args) {
				pushStateImpl.apply(this, args);
				triggerEvent();
			};

			let replaceStateImpl = history.replaceState;
			history.replaceState = function (...args) {
				replaceStateImpl.apply(this, args);
				triggerEvent();
			};
		}
		return this.observableLocationInternal;
	}

	private constructor() { }
}